import type { TranslationKey } from "@/lib/i18n";

/**
 * The project stages a built gate opens (R06): Resumen, 1 Planificar and
 * 2 Revisar y validar. The legacy tabs (installation, commissioning,
 * production, maintenance) stay mounted for old links, but no gate leads
 * there: gates 4–11 open nothing until their slice lands («Próximamente»).
 */
export type ProjectStageTab = "summary" | "planning" | "formulation";

export type ProjectNavLifecycleStatus = "completed" | "current" | "future";

/** Single user-facing entry point for opening an existing project. */
export function projectPlannerPath(projectId: string): string {
  return `/ops/projects/${projectId}/planning`;
}

/** The stage each gate opens; a gate missing here opens nothing yet. */
export const GATE_NAV_MAP: Readonly<Partial<Record<number, ProjectStageTab>>> =
  {
    1: "summary",
    2: "planning",
    3: "formulation",
  };

/**
 * Where a gate of the rail leads, or `null` when it leads nowhere: a gate not
 * built yet (`built: false`) is never navigable, whatever the map says.
 */
export function gateNavTarget(
  gate: number,
  built: boolean,
): ProjectStageTab | null {
  if (!built) return null;
  return GATE_NAV_MAP[gate] ?? null;
}

/** The gates during which each stage of the project sidebar is current. */
const PROJECT_STAGE_GATE_RANGES: Record<ProjectStageTab, [number, number]> = {
  summary: [1, 1],
  // Planning is the canonical first screen for a name-only project. Gate 1
  // metadata remains editable from Summary, but it does not make Planner a
  // future surface before the operator can define the area and catalogue.
  planning: [1, 2],
  // Revisar y validar closes gate 3: the two-person technical validation.
  formulation: [3, 3],
};

export const PROJECT_NAV_LIFECYCLE_STATUS_LABEL_KEYS: Record<
  ProjectNavLifecycleStatus,
  TranslationKey
> = {
  completed: "ops.lifecycle.completed",
  current: "ops.lifecycle.current",
  future: "ops.lifecycle.future",
};

function normalizeProjectStage(href: string): ProjectStageTab | null {
  const tab = href.replace(/^\/+/, "").split(/[/?#]/)[0];
  if (tab === "summary" || tab === "planning" || tab === "formulation") {
    return tab;
  }
  return null;
}

/**
 * The sidebar marker of a project stage at the given gate, or `null` for a
 * tab no gate maps to (Gemelo, the legacy tabs, the tools).
 */
export function getProjectNavLifecycleStatus(
  href: string,
  currentGate: number | undefined,
): ProjectNavLifecycleStatus | null {
  const tab = normalizeProjectStage(href);
  if (!tab || currentGate == null) return null;

  const [first, last] = PROJECT_STAGE_GATE_RANGES[tab];
  if (currentGate > last) return "completed";
  if (currentGate >= first) return "current";
  return "future";
}
